import React, { useCallback, useEffect, useState } from "react";
import { Alert, FormHelperText, Typography, useTheme } from "@mui/material";
import Autocomplete from "@components/Autocomplete/Autocomplete";
import DatePicker from "@components/DatePicker/DatePicker";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import { getSideDrawerStyles } from "@components/SideDrawer/SideDrawer.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import type { MovementError } from "@features/vessel-call/vessel-call-status/MovementServices/form/utils.ts";
import type { BerthAllocation, Movement } from "@services/movement-services/types";
import { getBerthAllocation } from "@services/movementServicesApi";
import { getAnchorageAreas } from "@services/referenceDataApi";
import type { VesselCallResponse } from "@services/vesselCall/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toMinute } from "@utils/date-time";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { z } from "zod";
import { isArrivalTerminalClaimedTaskOwner } from "../flow/utils";
import { useCompleteMovement } from "../hooks/useCompleteMovement";
import {
  getUniqueMovementUploadFiles,
  MAX_MOVEMENT_ATTACHMENTS,
  useMovementAttachmentUploads,
  type MovementAttachment,
} from "../hooks/useMovementAttachmentUploads";
import { useUpdateMovement } from "../hooks/useUpdateMovement";
import { getApprovalDrawerSchema } from "./ApproveMovement.schema";

const asDateOrNull = (v: unknown) => (v == null || v === "" ? null : (v as Date));

const isValidDate = (value: unknown): value is Date =>
  value instanceof Date && toMinute(value) !== null;

const isNotPastDateTime = (value: Date) => {
  const valueMinute = toMinute(value);
  const nowMinute = toMinute(new Date());

  return !!valueMinute && !!nowMinute && valueMinute.getTime() >= nowMinute.getTime();
};

const isNotFutureDateTime = (value: Date) => {
  const valueMinute = toMinute(value);
  const nowMinute = toMinute(new Date());

  return !!valueMinute && !!nowMinute && valueMinute.getTime() <= nowMinute.getTime();
};

const getNotPastDateTimeError = ({
  value,
  label,
  t,
}: {
  value: Date | null;
  label: string;
  t: (key: string, options?: Record<string, string>) => string;
}) => {
  if (!value) {
    return undefined;
  }

  if (!isValidDate(value)) {
    return t("form-validation.required", { label });
  }

  return isNotPastDateTime(value) ? undefined : t("form-validation.must-not-be-in-past", { label });
};

const getNotFutureDateTimeError = ({
  value,
  label,
  t,
}: {
  value: Date | null;
  label: string;
  t: (key: string, options?: Record<string, string>) => string;
}) => {
  if (!value) {
    return undefined;
  }

  if (!isValidDate(value)) {
    return t("form-validation.required", { label });
  }

  return isNotFutureDateTime(value)
    ? undefined
    : t("form-validation.must-not-be-in-future", { label });
};

const getEtdBeforeEtaError = ({
  etd,
  eta,
  t,
}: {
  etd: Date | null;
  eta: Date | null;
  t: (key: string) => string;
}) => {
  if (!isValidDate(etd) || !isValidDate(eta)) {
    return undefined;
  }

  return etd.getTime() < eta.getTime()
    ? t("vessel-management.vessel-status-page.etd") +
        t("vessel-management.vessel-status-page.equalOrGreater") +
        t("vessel-management.vessel-status-page.eta")
    : undefined;
};

// This form does not have a store defined, so all fields will be managed via local state or props
// => use regular input components and manage their state locally or via props
interface ApproveMovementServiceProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  currentMovements: Movement[];
}

export const FieldError: React.FC<{ children: React.ReactNode; testId?: string }> = ({
  children,
  testId,
}) => (
  <Typography
    variant="caption"
    color="error"
    sx={{ mt: 0.5, display: "block" }}
    data-testid={testId}
  >
    {children}
  </Typography>
);

const ToBerthElements: React.FC<{
  ets: Date | null;
  onChangeEts: (v: Date | null) => void;
  eta: Date | null;
  onChangeEta: (v: Date | null) => void;
  etd: Date | null;
  onChangeEtd: (v: Date | null) => void;
  errors?: { ets?: string; eta?: string; etd?: string };
}> = ({ ets, onChangeEts, eta, onChangeEta, etd, onChangeEtd, errors }) => {
  const { t } = useTranslation();
  return (
    <>
      <div className="py-3 flex flex-col">
        <CustomFormLabel>{t("vessel-management.vessel-status-page.ets")}</CustomFormLabel>
        <DatePicker
          quickCapture
          value={asDateOrNull(ets)}
          onChange={(d) => onChangeEts(d)}
          testId={`ets-date-input`}
          timePicker={true}
          dateConstraint="not-past"
          combinedInput={true}
          hasError={!!errors?.ets}
        />
        {errors?.ets && <FieldError testId="ets-error">{errors.ets}</FieldError>}
      </div>
      <div className="py-3 flex flex-col">
        <CustomFormLabel>{t("vessel-management.vessel-status-page.eta")}</CustomFormLabel>
        <DatePicker
          quickCapture
          value={asDateOrNull(eta)}
          onChange={(d) => onChangeEta(d)}
          testId={`eta-date-input`}
          timePicker={true}
          dateConstraint="not-past"
          combinedInput={true}
          hasError={!!errors?.eta}
        />
        {errors?.eta && <FieldError testId="eta-error">{errors.eta}</FieldError>}
      </div>
      <div className="py-3 flex flex-col">
        <CustomFormLabel>{t("vessel-management.vessel-status-page.etd")}</CustomFormLabel>
        <DatePicker
          quickCapture
          value={asDateOrNull(etd)}
          onChange={(d) => onChangeEtd(d)}
          testId={`etd-date-input`}
          timePicker={true}
          dateConstraint="not-past"
          minDate={eta ?? undefined}
          combinedInput={true}
          hasError={!!errors?.etd}
        />
        {errors?.etd && <FieldError testId="etd-error">{errors.etd}</FieldError>}
      </div>
    </>
  );
};

const BerthToBerthElements: React.FC<{
  eta: Date | null;
  onChangeEta: (value: Date | null) => void;
  etaLabel: string;
  ets: Date | null;
  onChangeEts: (value: Date | null) => void;
  etsLabel: string;
  etd: Date | null;
  onChangeEtd: (value: Date | null) => void;
  etdLabel: string;
  errors?: { eta?: string; ets?: string; etd?: string };
}> = ({
  eta,
  onChangeEta,
  etaLabel,
  ets,
  onChangeEts,
  etsLabel,
  etd,
  onChangeEtd,
  etdLabel,
  errors,
}) => (
  <>
    <div className="py-3 flex flex-col">
      <CustomFormLabel>{etaLabel}</CustomFormLabel>
      <DatePicker
        quickCapture
        value={asDateOrNull(eta)}
        onChange={onChangeEta}
        testId="eta-date-input"
        timePicker
        dateConstraint="not-past"
        combinedInput
        hasError={!!errors?.eta}
      />
      {errors?.eta && <FieldError testId="eta-error">{errors.eta}</FieldError>}
    </div>
    <div className="py-3 flex flex-col">
      <CustomFormLabel>{etsLabel}</CustomFormLabel>
      <DatePicker
        quickCapture
        value={asDateOrNull(ets)}
        onChange={onChangeEts}
        testId="ets-date-input"
        timePicker
        dateConstraint="not-past"
        combinedInput
        hasError={!!errors?.ets}
      />
      {errors?.ets && <FieldError testId="ets-error">{errors.ets}</FieldError>}
    </div>
    <div className="py-3 flex flex-col">
      <CustomFormLabel>{etdLabel}</CustomFormLabel>
      <DatePicker
        quickCapture
        value={asDateOrNull(etd)}
        onChange={onChangeEtd}
        testId="etd-date-input"
        timePicker
        dateConstraint="not-past"
        minDate={eta ?? undefined}
        combinedInput
        hasError={!!errors?.etd}
      />
      {errors?.etd && <FieldError testId="etd-error">{errors.etd}</FieldError>}
    </div>
  </>
);

const SeaToBerthElements: React.FC<{
  eta: Date | null;
  onChangeEta: (v: Date | null) => void;
  etaLabel: string;
  ets: Date | null;
  onChangeEts: (v: Date | null) => void;
  etsLabel: string;
  errors?: { eta?: string; ets?: string };
}> = ({ eta, onChangeEta, etaLabel, ets, onChangeEts, etsLabel, errors }) => {
  return (
    <>
      <div className="py-3 flex flex-col">
        <CustomFormLabel>{etaLabel}</CustomFormLabel>
        <DatePicker
          quickCapture
          value={asDateOrNull(eta)}
          onChange={(d) => onChangeEta(d)}
          testId={`eta-date-input`}
          timePicker={true}
          dateConstraint="not-past"
          combinedInput={true}
          hasError={!!errors?.eta}
        />
        {errors?.eta && <FieldError testId="eta-error">{errors.eta}</FieldError>}
      </div>
      <div className="py-3 flex flex-col">
        <CustomFormLabel>{etsLabel}</CustomFormLabel>
        <DatePicker
          quickCapture
          value={asDateOrNull(ets)}
          onChange={(d) => onChangeEts(d)}
          testId={`ets-date-input`}
          timePicker={true}
          dateConstraint="not-past"
          combinedInput={true}
          hasError={!!errors?.ets}
        />
        {errors?.ets && <FieldError testId="ets-error">{errors.ets}</FieldError>}
      </div>
    </>
  );
};

const BerthToSeaElements: React.FC<{
  etd: Date | null;
  onChangeEtd: (value: Date | null) => void;
  etdLabel: string;
  ets: Date | null;
  onChangeEts: (value: Date | null) => void;
  etsLabel: string;
  errors?: { etd?: string; ets?: string };
}> = ({ etd, onChangeEtd, etdLabel, ets, onChangeEts, etsLabel, errors }) => (
  <>
    <div className="py-3 flex flex-col">
      <CustomFormLabel>{etdLabel}</CustomFormLabel>
      <DatePicker
        quickCapture
        value={asDateOrNull(etd)}
        onChange={onChangeEtd}
        testId="etd-date-input"
        timePicker
        dateConstraint="not-past"
        combinedInput
        hasError={!!errors?.etd}
      />
      {errors?.etd && <FieldError testId="etd-error">{errors.etd}</FieldError>}
    </div>
    <div className="py-3 flex flex-col">
      <CustomFormLabel>{etsLabel}</CustomFormLabel>
      <DatePicker
        quickCapture
        value={asDateOrNull(ets)}
        onChange={onChangeEts}
        testId="ets-date-input"
        timePicker
        dateConstraint="not-past"
        combinedInput
        hasError={!!errors?.ets}
      />
      {errors?.ets && <FieldError testId="ets-error">{errors.ets}</FieldError>}
    </div>
  </>
);

const ToAnchorageElements: React.FC<{
  isSeaToAnchorage: boolean;
  isBerthToAnchorage: boolean;
  etd: Date | null;
  etdLabel: string;
  onChangeEtd: (v: Date | null) => void;
  etdError?: string;
  ets: Date | null;
  etsLabel: string;
  onChangeEts: (v: Date | null) => void;
  etsError?: string;
  ats: Date | null;
  atsLabel: string;
  onChangeAts: (v: Date | null) => void;
  atsError?: string;
  designatedArea: string;
  designatedAreaLabel: string;
  onChangeDesignedArea: (v: string) => void;
  toAnchorageLocation?: string;
  errors?: { designatedArea?: string };
}> = ({
  isSeaToAnchorage,
  isBerthToAnchorage,
  etd,
  etdLabel,
  onChangeEtd,
  etdError,
  ets,
  etsLabel,
  onChangeEts,
  etsError,
  ats,
  atsLabel,
  onChangeAts,
  atsError,
  designatedArea,
  designatedAreaLabel,
  onChangeDesignedArea,
  toAnchorageLocation,
  errors,
}) => {
  const { t } = useTranslation();
  const { data: anchorageAreas } = useQuery({
    queryKey: ["anchorageAreas"],
    queryFn: () => getAnchorageAreas("bff-vessel-call"),
  });

  const anchorageOptions: { value: string; label: string }[] =
    anchorageAreas
      ?.filter((option) => option.fromtoloc?.toLowerCase() === toAnchorageLocation?.toLowerCase())
      .map((option) => ({
        value: option.anchorageAreaCode,
        label: option.anchorageAreaDesc,
      })) || [];
  const selectedOption = anchorageOptions.find((option) => option.value === designatedArea) || null;
  return (
    <>
      {isBerthToAnchorage && (
        <div className="py-3 flex flex-col">
          <CustomFormLabel>{etdLabel}</CustomFormLabel>
          <DatePicker
            quickCapture
            value={asDateOrNull(etd)}
            onChange={onChangeEtd}
            testId="etd-date-input"
            timePicker
            dateConstraint="not-past"
            combinedInput
            hasError={!!etdError}
          />
          {etdError && <FieldError testId="anchorage-etd-error">{etdError}</FieldError>}
        </div>
      )}
      <div className="py-3 flex flex-col">
        {isSeaToAnchorage ? (
          <>
            <CustomFormLabel>{atsLabel}</CustomFormLabel>
            <DatePicker
              quickCapture
              value={asDateOrNull(ats)}
              onChange={onChangeAts}
              testId="ats-date-input"
              timePicker={true}
              dateConstraint="not-future"
              combinedInput={true}
              hasError={!!atsError}
            />
            {atsError && <FieldError testId="anchorage-ats-error">{atsError}</FieldError>}
          </>
        ) : (
          <>
            <CustomFormLabel>{etsLabel}</CustomFormLabel>
            <DatePicker
              quickCapture
              value={asDateOrNull(ets)}
              onChange={onChangeEts}
              testId="ets-date-input"
              timePicker={true}
              dateConstraint="not-past"
              combinedInput={true}
              hasError={!!etsError}
            />
            {etsError && <FieldError testId="anchorage-ets-error">{etsError}</FieldError>}
          </>
        )}
      </div>
      <div className="py-3 flex flex-col">
        <CustomFormLabel>{designatedAreaLabel}</CustomFormLabel>
        <Autocomplete
          value={selectedOption}
          onChange={(d) => onChangeDesignedArea(d?.value != null ? String(d.value) : "")}
          options={anchorageOptions}
          testId={`designated-area-input`}
          placeholder={t("vessel-management.vessel-status-page.assignedAnchorageArea")}
        />
        {errors?.designatedArea && (
          <FieldError testId="anchorage-designatedArea-error">{errors.designatedArea}</FieldError>
        )}
      </div>
    </>
  );
};
interface OutletContextType {
  vesselCallDetails: VesselCallResponse;
}
const serviceVesselCall = "bff-vessel-call";

const ApproveMovementService: React.FC<ApproveMovementServiceProps> = ({
  isOpen,
  onClose,
  currentMovements,
}) => {
  const { vesselCallDetails } = useOutletContext<OutletContextType>();
  const vesselId = vesselCallDetails?.vesselId;
  const vesselCallId = vesselCallDetails?.id;
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const currentMovement = currentMovements?.[0];
  const isToAnchorage = currentMovement?.to === "FOAA" || currentMovement?.to === "DOAA";

  const isSeaToBerth = currentMovement?.movementType === "SEA_TO_BERTH";
  const isBerthToSea = currentMovement?.movementType === "BERTH_TO_SEA";
  const isAnchorageToSea = currentMovement?.movementType === "ANCHORAGE_TO_SEA";
  const isAnchorageToBerth = currentMovement?.movementType === "ANCHORAGE_TO_BERTH";
  const isAnchorageToAnchorage = currentMovement?.movementType === "ANCHORAGE_TO_ANCHORAGE";
  const isSeaToAnchorage = currentMovement?.movementType === "SEA_TO_ANCHORAGE";
  const isBerthToBerth = currentMovement?.movementType === "BERTH_TO_BERTH";
  const isBerthToAnchorage = currentMovement?.movementType === "BERTH_TO_ANCHORAGE";

  let toAnchorageLocation;

  if (isToAnchorage) {
    toAnchorageLocation = currentMovement?.to;
  }
  const isArrivalTerminal = isArrivalTerminalClaimedTaskOwner(currentMovement?.status ?? "");
  const { data: berthAllocation } = useQuery<BerthAllocation[]>({
    queryKey: ["berth-allocation", currentMovement?.to],
    queryFn: async () => {
      const result = await getBerthAllocation({
        service: "bff-vessel-call",
        fromTo: currentMovement?.to,
      });
      return Array.isArray(result) ? result : [result];
    },
  });
  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const { t } = useTranslation();
  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
  const TEXTAREA_MAX_CHARS = 1000;
  const [additionalErrors, setAdditionalErrors] = useState<{
    externalNotes?: string;
    externalFiles?: string;
    berthPlanFile?: string;
    internalAttachmentsFile?: string;
    internalNotes?: string;
    ets?: string;
    ats?: string;
    eta?: string;
    etd?: string;
    permissionToSailTime?: string;
    designatedArea?: string;
    rta?: string;
    berthAllocationArea?: string;
  }>({});
  const theme = useTheme();
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const profile = FILE_UPLOAD_PROFILES.sideDrawerApprovalFile;
  const allowedSideDrawerExtensions = profile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);
  const inputSlotProps = {
    root: {
      className: `rounded-lg text-sm px-4 py-2 border`,
      style: {
        borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
        color: theme.palette.text.primary,
      },
    },
    htmlInput: {
      maxLength: TEXTAREA_MAX_CHARS,
    },
  };
  const attachmentUploads = useMovementAttachmentUploads(
    "ApproveMovementService",
    "externalAttachmentsFile",
  );
  const berthPlanUploads = useMovementAttachmentUploads("ApproveMovementService", "berthPlanFile");
  const internalAttachmentUploads = useMovementAttachmentUploads(
    "ApproveMovementService",
    "internalAttachmentsFile",
  );
  const berthPlanFile = useUploadStore(
    (state) => state.filesByKey.berthPlanFile ?? EMPTY_UPLOAD_ENTRIES,
  );
  const internalAttachmentsFile = useUploadStore(
    (state) => state.filesByKey.internalAttachmentsFile ?? EMPTY_UPLOAD_ENTRIES,
  );
  const externalAttachmentsFile = useUploadStore(
    (state) => state.filesByKey.externalAttachmentsFile ?? EMPTY_UPLOAD_ENTRIES,
  );
  const [permissionToSailTime, setPermissionToSailTime] = useState<Date | null>(null);
  const [ets, setEts] = useState<Date | null>(null);
  const [ats, setAts] = useState<Date | null>(null);
  const [eta, setEta] = useState<Date | null>(null);
  const [etd, setEtd] = useState<Date | null>(null);
  const [rta, setRta] = useState<Date | null>(null);
  const [designatedArea, setDesignatedArea] = useState<string>("");
  const [berthAllocationArea, setBerthAllocationArea] = useState<string>("");
  const berthAllocationOptions: { value: string; label: string }[] =
    berthAllocation?.map((option) => ({
      value: option.code,
      label: option.desc,
    })) || [];

  const selectedOption =
    berthAllocationOptions.find((option) => option.value === berthAllocationArea) || null;

  useEffect(() => {
    if (isOpen) {
      setBerthAllocationArea(currentMovement?.toAreaBerth ?? "");
      if (isSeaToAnchorage) {
        setDesignatedArea(vesselCallDetails?.assignedAnchorageArea?.anchorageAreaCode ?? "");
        setAts(
          vesselCallDetails?.lastTaskDecision?.timestamp
            ? new Date(vesselCallDetails.lastTaskDecision.timestamp)
            : null,
        );
      }
    }
  }, [
    currentMovement?.toAreaBerth,
    currentMovement?.id,
    isOpen,
    isSeaToAnchorage,
    vesselCallDetails?.assignedAnchorageArea?.anchorageAreaCode,
    vesselCallDetails?.lastTaskDecision?.timestamp,
  ]);

  const sxProps = {
    "& .MuiInputBase-root": {
      borderRadius: "8px",
      borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
      padding: "12px",
      fontSize: theme.typography.body1.fontSize,
      color: theme.palette.text.primary,
    },
    "& textarea": {
      padding: 0,
    },
  };

  const styles = getSideDrawerStyles(theme);
  const internalNotesOnlySchema = getApprovalDrawerSchema(t).pick({ internalNotes: true });
  const externalNotesOnlySchema = getApprovalDrawerSchema(t).pick({ externalNotes: true });
  const handleFileUpload = ({
    newFiles,
    errorKey,
    setUploadFiles,
    onUploadDocument,
  }: {
    newFiles: File[];
    errorKey: "berthPlanFile" | "internalAttachmentsFile";
    setUploadFiles: (files: UploadEntry[]) => void;
    onUploadDocument: (file: File) => void;
  }) => {
    if (newFiles.length > 1) {
      setAdditionalErrors((prev) => ({ ...prev, [errorKey]: t("upload.one-document") }));
      return;
    }

    const file = newFiles[0];
    if (!file) {
      return;
    }

    setAdditionalErrors((prev) => ({ ...prev, [errorKey]: undefined }));
    setUploadFiles([file]);
    onUploadDocument(file);
  };

  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };
  const [externalNotes, setExternalNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const setSideDrawerAdditionalFile = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("sideDrawerAdditionalFile");
        return;
      }
      setFilesForKey("sideDrawerAdditionalFile", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const setBerthPlanFile = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("berthPlanFile");
        return;
      }
      setFilesForKey("berthPlanFile", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const setInternalAttachmentsFile = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("internalAttachmentsFile");
        return;
      }
      setFilesForKey("internalAttachmentsFile", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const setExternalAttachmentsFile = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("externalAttachmentsFile");
        return;
      }
      setFilesForKey("externalAttachmentsFile", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  useEffect(() => {
    setSideDrawerAdditionalFile([]);
    setBerthPlanFile([]);
    setExternalAttachmentsFile([]);
    setInternalAttachmentsFile([]);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const existingBerthPlan =
      isSeaToBerth || isAnchorageToBerth ? currentMovement?.berthPlan?.[0] : undefined;

    if (!existingBerthPlan) {
      setBerthPlanFile([]);
      return;
    }

    setBerthPlanFile([
      {
        name: existingBerthPlan.originalFileName || "Berth Plan",
        url: existingBerthPlan.url,
        type: existingBerthPlan.type,
      },
    ]);
  }, [
    currentMovement?.berthPlan,
    currentMovement?.id,
    isOpen,
    isSeaToBerth,
    isAnchorageToBerth,
    setBerthPlanFile,
  ]);

  const resetFormValues = () => {
    attachmentUploads.reset();
    berthPlanUploads.reset();
    internalAttachmentUploads.reset();
    setPermissionToSailTime(null);
    setEts(null);
    setAts(null);
    setEta(null);
    setEtd(null);
    setRta(null);
    setDesignatedArea("");
    setBerthAllocationArea("");
    setExternalNotes("");
    setInternalNotes("");
    setSideDrawerAdditionalFile([]);
    setBerthPlanFile([]);
    setExternalAttachmentsFile([]);
    setInternalAttachmentsFile([]);
    setAdditionalErrors({});
  };

  const requiredNotPastDateTime = (label: string) => {
    const requiredMessage = t("form-validation.required", { label });

    return z
      .date({
        required_error: requiredMessage,
        invalid_type_error: requiredMessage,
      })
      .refine(isNotPastDateTime, {
        message: t("form-validation.must-not-be-in-past", { label }),
      });
  };

  const requiredNotFutureDateTime = (label: string) => {
    const requiredMessage = t("form-validation.required", { label });

    return z
      .date({
        required_error: requiredMessage,
        invalid_type_error: requiredMessage,
      })
      .refine(isNotFutureDateTime, {
        message: t("form-validation.must-not-be-in-future", { label }),
      });
  };

  const etsLabel =
    isSeaToBerth || isBerthToSea || isAnchorageToBerth || isBerthToAnchorage || isBerthToBerth
      ? t("vessel-call.movement-services.preview.fields.ets-readiness")
      : isAnchorageToAnchorage
        ? t("vessel-call.movement-services.preview.fields.ets-permission-to-heave-anchor")
        : t("vessel-management.vessel-status-page.ets");
  const atsLabel = t("vessel-call.movement-services.preview.fields.ats-permission-to-anchor");

  const etdLabel = isBerthToAnchorage
    ? t("vessel-call.movement-services.preview.fields.etd-readiness")
    : isBerthToBerth
      ? t("vessel-call.movement-services.preview.fields.etd-vessel")
      : isBerthToSea
        ? t("vessel-call.movement-services.preview.fields.etd-readiness")
        : t("vessel-call.movement-services.preview.fields.etd");

  const etaLabel = isBerthToBerth
    ? t("vessel-call.movement-services.preview.fields.eta-vessel")
    : isSeaToBerth || isAnchorageToBerth
      ? t("vessel-call.movement-services.preview.fields.eta-readiness")
      : t("vessel-management.vessel-status-page.eta");

  const rtaLabel =
    isSeaToBerth || isAnchorageToBerth || isBerthToBerth
      ? t("vessel-call.movement-services.preview.fields.rta-readiness")
      : t("vessel-call.movement-services.preview.fields.rta");

  const designatedAreaLabel =
    isSeaToAnchorage || isBerthToAnchorage || isAnchorageToAnchorage
      ? t("vessel-management.vessel-status-page.assignedAnchorageArea")
      : t("vessel-management.vessel-status-page.designatedArea");

  const permissionToSailTimeLabel = t(
    "vessel-call.movement-services.preview.fields.ats-permission-to-sail",
  );
  const berthSchema = z
    .object({
      ets: requiredNotPastDateTime(etsLabel),
      eta: requiredNotPastDateTime(etaLabel),
      etd: z.date().nullable().optional(),
    })
    .superRefine((data, ctx) => {
      if (isSeaToBerth) return;

      const etdError = getEtdBeforeEtaError({ etd: data.etd ?? null, eta: data.eta, t });

      if (etdError) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["etd"],
          message: etdError,
        });
      }
    });

  const anchorageSchema = z.object({
    ets: requiredNotPastDateTime(etsLabel),
    designatedArea: z.string().min(1, {
      message: t("form-validation.required", {
        label: designatedAreaLabel,
      }),
    }),
  });
  const berthToAnchorageSchema = z.object({
    etd: requiredNotPastDateTime(etdLabel),
    ets: requiredNotPastDateTime(etsLabel),
    designatedArea: z.string().min(1, {
      message: t("form-validation.required", {
        label: designatedAreaLabel,
      }),
    }),
  });
  const seaToAnchorageSchema = z.object({
    ats: requiredNotFutureDateTime(atsLabel),
    designatedArea: z.string().min(1, {
      message: t("form-validation.required", {
        label: designatedAreaLabel,
      }),
    }),
  });
  const berthToSeaSchema = z.object({
    etd: requiredNotPastDateTime(etdLabel),
    ets: requiredNotPastDateTime(etsLabel),
  });
  const arrivalTerminalSchema = z.object({
    rta: requiredNotPastDateTime(rtaLabel),
    berthAllocationArea: z.string().min(1, {
      message: t("form-validation.required", {
        label: t("vessel-management.vessel-status-page.berthAllocation"),
      }),
    }),
  });
  const seaSchema = z.object({
    permissionToSailTime: requiredNotFutureDateTime(permissionToSailTimeLabel),
  });
  const handleSuccess = async () => {
    setTimeout(async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
      });

      // Invalidate and refetch the preview
      const movementServiceId = currentMovement.id;
      if (movementServiceId) {
        await queryClient.invalidateQueries({
          queryKey: ["movement-service-preview", movementServiceId],
        });
        await queryClient.refetchQueries({
          queryKey: ["movement-service-preview", movementServiceId],
        });
      }
    }, 2000);
    onClose("approveDrawer");
    resetFormValues();
    notify({
      title: t("vessel-call.movement-services.movement-approved"),
      type: "success",
    });
  };

  const handleCloseDrawer = () => {
    onClose("approveDrawer");
    resetFormValues();
  };

  const handleError = async (error: MovementError) => {
    await queryClient.invalidateQueries({
      queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
    });

    // Invalidate and refetch the preview
    const movementServiceId = currentMovement.id;
    if (movementServiceId) {
      await queryClient.invalidateQueries({
        queryKey: ["movement-service-preview", movementServiceId],
      });
      await queryClient.refetchQueries({
        queryKey: ["movement-service-preview", movementServiceId],
      });
    }

    notify({
      title: t("common.error"),
      description:
        error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
      type: "error",
    });
    if (error.status === 409) {
      handleCloseDrawer();
    }
  };
  const { completeMovement } = useCompleteMovement({
    handleSuccess,
    handleError,
  });

  const { updateMovement } = useUpdateMovement({
    handleSuccess,
    handleError,
  });
  const handleSubmit = async () => {
    if (
      attachmentUploads.isPending ||
      berthPlanUploads.isPending ||
      internalAttachmentUploads.isPending
    ) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }
    const incompleteUpload = [
      { files: externalAttachmentsFile, error: attachmentUploads.error },
      { files: berthPlanFile, error: berthPlanUploads.error },
      { files: internalAttachmentsFile, error: internalAttachmentUploads.error },
    ].find(({ files }) => files.some((file) => file instanceof File || !file.url));
    if (incompleteUpload) {
      notify({
        title: t("common.error"),
        description: incompleteUpload.error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
      return;
    }

    const payload: {
      status: "APPROVED";
      ats?: string;
      ets?: string;
      eta?: string;
      etd?: string;
      designatedArea?: string;
      rta?: string;
      berthAllocation?: string;
      externalNotes?: string;
      externalAttachments?: MovementAttachment[];
      internalNotes?: string;
      internalAttachments?: [
        {
          originalFileName: string;
          url: string;
          type: string;
        },
      ];
      berthPlan?: [
        {
          originalFileName: string;
          url: string;
          type: string;
        },
      ];
    } = {
      status: "APPROVED",
    };

    // When arrival terminal, skip location-specific validations so RTA/berthAllocation errors can surface
    if (!isArrivalTerminal) {
      if (isToAnchorage) {
        if (isSeaToAnchorage) {
          const result = seaToAnchorageSchema.safeParse({ ats, designatedArea });
          if (!result.success) {
            const fieldErrors = result.error.flatten().fieldErrors;
            setAdditionalErrors((prev) => ({
              ...prev,
              ats: fieldErrors.ats?.[0],
              designatedArea: fieldErrors.designatedArea?.[0],
            }));
            return;
          }
          payload.ats = result.data.ats.toISOString();
          payload.designatedArea = result.data.designatedArea;
        } else if (isBerthToAnchorage) {
          const result = berthToAnchorageSchema.safeParse({ etd, ets, designatedArea });
          if (!result.success) {
            const fieldErrors = result.error.flatten().fieldErrors;
            setAdditionalErrors((prev) => ({
              ...prev,
              etd: fieldErrors.etd?.[0],
              ets: fieldErrors.ets?.[0],
              designatedArea: fieldErrors.designatedArea?.[0],
            }));
            return;
          }
          payload.etd = result.data.etd.toISOString();
          payload.ets = result.data.ets.toISOString();
          payload.designatedArea = result.data.designatedArea;
        } else {
          const result = anchorageSchema.safeParse({ ets, designatedArea });
          if (!result.success) {
            const fieldErrors = result.error.flatten().fieldErrors;
            setAdditionalErrors((prev) => ({
              ...prev,
              ets: fieldErrors.ets?.[0],
              designatedArea: fieldErrors.designatedArea?.[0],
            }));
            return;
          }
          payload.ets = result.data.ets.toISOString();
          payload.designatedArea = result.data.designatedArea;
        }
        setAdditionalErrors((prev) => ({
          ...prev,
          ets: undefined,
          ats: undefined,
          designatedArea: undefined,
        }));
      } else if (isBerthToSea) {
        const result = berthToSeaSchema.safeParse({ etd, ets });
        if (!result.success) {
          const fieldErrors = result.error.flatten().fieldErrors;
          setAdditionalErrors((prev) => ({
            ...prev,
            etd: fieldErrors.etd?.[0],
            ets: fieldErrors.ets?.[0],
          }));
          return;
        }
        payload.etd = result.data.etd.toISOString();
        payload.ets = result.data.ets.toISOString();
        setAdditionalErrors((prev) => ({ ...prev, etd: undefined, ets: undefined }));
      } else if (currentMovement?.to !== "SEA") {
        const result = berthSchema.safeParse({ ets, eta, etd });
        if (!result.success) {
          const fieldErrors = result.error.flatten().fieldErrors;
          setAdditionalErrors((prev) => ({
            ...prev,
            ets: fieldErrors.ets?.[0],
            eta: fieldErrors.eta?.[0],
            etd: fieldErrors.etd?.[0],
          }));
          return; // stop submit when invalid
        }
        payload.ets = result.data.ets.toISOString();
        payload.eta = result.data.eta.toISOString();
        if (!isSeaToBerth && result.data.etd) {
          payload.etd = result.data.etd.toISOString();
        }
        // clear previous errors on success
        setAdditionalErrors((prev) => ({
          ...prev,
          ets: undefined,
          eta: undefined,
          etd: undefined,
        }));
      }
    }
    if (isAnchorageToSea) {
      const result = seaSchema.safeParse({ permissionToSailTime });
      if (!result.success) {
        const fieldErrors = result.error.flatten().fieldErrors;
        setAdditionalErrors((prev) => ({
          ...prev,
          permissionToSailTime: fieldErrors.permissionToSailTime?.[0],
        }));
        return;
      }
      payload.ats = result.data.permissionToSailTime.toISOString();
      setAdditionalErrors((prev) => ({ ...prev, permissionToSailTime: undefined }));
    }
    if (isArrivalTerminal) {
      const result = arrivalTerminalSchema.safeParse({
        rta,
        berthAllocationArea,
      });
      if (!result.success) {
        const fieldErrors = result.error.flatten().fieldErrors;
        setAdditionalErrors((prev) => ({
          ...prev,
          rta: fieldErrors.rta?.[0],
          berthAllocationArea: fieldErrors.berthAllocationArea?.[0],
        }));
        return;
      }
      payload.rta = result.data.rta.toISOString();
      payload.berthAllocation = result.data.berthAllocationArea;
      setAdditionalErrors((prev) => ({ ...prev, rta: undefined, berthAllocationArea: undefined }));
      if (berthPlanFile[0] && !(berthPlanFile[0] instanceof File) && berthPlanFile[0].url) {
        payload.berthPlan = [
          {
            originalFileName: berthPlanFile[0]?.name || "attachment",
            url: berthPlanFile[0].url,
            type: "BERTH_PLAN",
          },
        ];
      }
      payload.externalNotes = externalNotes;
    } else {
      if (
        internalAttachmentsFile[0] &&
        !(internalAttachmentsFile[0] instanceof File) &&
        internalAttachmentsFile[0].url
      ) {
        payload.internalAttachments = [
          {
            originalFileName: internalAttachmentsFile[0]?.name || "attachment",
            url: internalAttachmentsFile[0].url,
            type: "INTERNAL_ATTACHMENT",
          },
        ];
      }
      payload.externalNotes = externalNotes;
      payload.internalNotes = internalNotes;
    }
    if (externalAttachmentsFile.length) {
      payload.externalAttachments = externalAttachmentsFile.flatMap((file) =>
        file instanceof File || !file.url
          ? []
          : [
              {
                originalFileName: file.name || "attachment",
                url: file.url,
                type: "EXTERNAL_ATTACHMENT",
              },
            ],
      );
    }
    if (isArrivalTerminal) {
      await completeMovement({
        id: currentMovement.id || "",
        service: serviceVesselCall,
        payload,
      });
    } else {
      await updateMovement({
        id: currentMovement.id || "",
        service: serviceVesselCall,
        payload: payload,
      });
    }
  };

  return (
    <div data-testid="approve-movement-service-form" className="max-h-screen overflow-y-auto">
      <SideDrawer
        isOpen={isOpen}
        title={t(`vessel-call.movement-services.approve-title`)}
        onClose={handleCloseDrawer}
        submitLabel={t(`vessel-call.movement-services.approve-button`)}
        onSubmit={handleSubmit}
        keepMounted={false}
      >
        <div className="flex-1 max-w-sm">
          <div className="px-6 pb-4">
            <Typography
              variant="body1"
              data-testid="movement-service-form-title"
              sx={{ color: "text.primary", pb: 4 }}
            >
              {t(`vessel-call.movement-services.approve-subtitle`)}
            </Typography>
            {isBerthToSea && !isArrivalTerminal && (
              <BerthToSeaElements
                etd={etd}
                etdLabel={etdLabel}
                onChangeEtd={(value) => {
                  setEtd(value);
                  setAdditionalErrors((previous) => ({
                    ...previous,
                    etd: getNotPastDateTimeError({ value, label: etdLabel, t }),
                  }));
                }}
                ets={ets}
                etsLabel={etsLabel}
                onChangeEts={(value) => {
                  setEts(value);
                  setAdditionalErrors((previous) => ({
                    ...previous,
                    ets: getNotPastDateTimeError({ value, label: etsLabel, t }),
                  }));
                }}
                errors={{ etd: additionalErrors.etd, ets: additionalErrors.ets }}
              />
            )}
            {isAnchorageToSea && (
              <div className="py-3 flex flex-col">
                <CustomFormLabel>{permissionToSailTimeLabel}</CustomFormLabel>
                <DatePicker
                  quickCapture
                  value={asDateOrNull(permissionToSailTime)}
                  onChange={(d) => {
                    setPermissionToSailTime(d);
                    setAdditionalErrors((p) => ({
                      ...p,
                      permissionToSailTime: undefined,
                    }));
                  }}
                  testId={`permission-to-sail-time-date-input`}
                  timePicker={true}
                  dateConstraint="not-future"
                  combinedInput={true}
                  hasError={!!additionalErrors.permissionToSailTime}
                />
                {additionalErrors.permissionToSailTime !== undefined && (
                  <FieldError testId="permission-to-sail-time-error">
                    {additionalErrors.permissionToSailTime}
                  </FieldError>
                )}
              </div>
            )}
            {!isToAnchorage && currentMovement?.to !== "SEA" && !isArrivalTerminal && (
              <>
                {isSeaToBerth || isAnchorageToBerth ? (
                  <SeaToBerthElements
                    eta={eta}
                    etaLabel={etaLabel}
                    onChangeEta={(d) => {
                      setEta(d);
                      setAdditionalErrors((p) => ({
                        ...p,
                        eta: getNotPastDateTimeError({ value: d, label: etaLabel, t }),
                        etd: undefined,
                      }));
                    }}
                    ets={ets}
                    etsLabel={etsLabel}
                    onChangeEts={(d) => {
                      setEts(d);
                      setAdditionalErrors((p) => ({
                        ...p,
                        ets: getNotPastDateTimeError({ value: d, label: etsLabel, t }),
                      }));
                    }}
                    errors={{
                      eta: additionalErrors.eta,
                      ets: additionalErrors.ets,
                    }}
                  />
                ) : isBerthToBerth ? (
                  <BerthToBerthElements
                    eta={eta}
                    etaLabel={etaLabel}
                    onChangeEta={(d) => {
                      setEta(d);
                      setAdditionalErrors((p) => ({
                        ...p,
                        eta: getNotPastDateTimeError({ value: d, label: etaLabel, t }),
                        etd: getEtdBeforeEtaError({ etd, eta: d, t }),
                      }));
                    }}
                    ets={ets}
                    etsLabel={etsLabel}
                    onChangeEts={(d) => {
                      setEts(d);
                      setAdditionalErrors((p) => ({
                        ...p,
                        ets: getNotPastDateTimeError({ value: d, label: etsLabel, t }),
                      }));
                    }}
                    etd={etd}
                    etdLabel={etdLabel}
                    onChangeEtd={(d) => {
                      setEtd(d);
                      setAdditionalErrors((p) => ({
                        ...p,
                        etd: getEtdBeforeEtaError({ etd: d, eta, t }),
                      }));
                    }}
                    errors={{
                      eta: additionalErrors.eta,
                      ets: additionalErrors.ets,
                      etd: additionalErrors.etd,
                    }}
                  />
                ) : (
                  <ToBerthElements
                    ets={ets}
                    onChangeEts={(d) => {
                      setEts(d);
                      setAdditionalErrors((p) => ({
                        ...p,
                        ets: getNotPastDateTimeError({ value: d, label: etsLabel, t }),
                      }));
                    }}
                    eta={eta}
                    onChangeEta={(d) => {
                      setEta(d);
                      setAdditionalErrors((p) => ({
                        ...p,
                        eta: getNotPastDateTimeError({ value: d, label: etaLabel, t }),
                        etd: getEtdBeforeEtaError({ etd, eta: d, t }),
                      }));
                    }}
                    etd={etd}
                    onChangeEtd={(d) => {
                      setEtd(d);
                      setAdditionalErrors((p) => ({
                        ...p,
                        etd: getEtdBeforeEtaError({ etd: d, eta, t }),
                      }));
                    }}
                    errors={{
                      ets: additionalErrors.ets,
                      eta: additionalErrors.eta,
                      etd: additionalErrors.etd,
                    }}
                  />
                )}
              </>
            )}
            {isToAnchorage && !isArrivalTerminal && (
              <ToAnchorageElements
                isSeaToAnchorage={isSeaToAnchorage}
                isBerthToAnchorage={isBerthToAnchorage}
                etd={etd}
                etdLabel={etdLabel}
                onChangeEtd={(d) => {
                  setEtd(d);
                  setAdditionalErrors((p) => ({
                    ...p,
                    etd: getNotPastDateTimeError({ value: d, label: etdLabel, t }),
                  }));
                }}
                etdError={additionalErrors.etd}
                ets={ets}
                etsLabel={etsLabel}
                onChangeEts={(d) => {
                  setEts(d);
                  setAdditionalErrors((p) => ({
                    ...p,
                    ets: getNotPastDateTimeError({ value: d, label: etsLabel, t }),
                  }));
                }}
                etsError={additionalErrors.ets}
                ats={ats}
                atsLabel={atsLabel}
                onChangeAts={(d) => {
                  setAts(d);
                  setAdditionalErrors((p) => ({
                    ...p,
                    ats: getNotFutureDateTimeError({ value: d, label: atsLabel, t }),
                  }));
                }}
                atsError={additionalErrors.ats}
                designatedArea={designatedArea}
                designatedAreaLabel={designatedAreaLabel}
                toAnchorageLocation={toAnchorageLocation}
                onChangeDesignedArea={(val) => {
                  setDesignatedArea(val);
                  if (val) setAdditionalErrors((p) => ({ ...p, designatedArea: undefined }));
                }}
                errors={{
                  designatedArea: additionalErrors.designatedArea,
                }}
              />
            )}
            {isArrivalTerminal && (
              <>
                <div className="py-3 flex flex-col">
                  <CustomFormLabel>{rtaLabel}</CustomFormLabel>
                  <DatePicker
                    quickCapture
                    value={asDateOrNull(rta)}
                    onChange={(d) => {
                      setRta(d);
                      setAdditionalErrors((p) => ({
                        ...p,
                        rta: getNotPastDateTimeError({ value: d, label: rtaLabel, t }),
                      }));
                    }}
                    testId={`rta-date-input`}
                    timePicker={true}
                    dateConstraint="not-past"
                    combinedInput={true}
                    hasError={!!additionalErrors.rta}
                  />
                  {additionalErrors.rta !== undefined && (
                    <FieldError testId="rta-error">{additionalErrors.rta}</FieldError>
                  )}
                </div>
                <div className="py-3 flex flex-col">
                  <CustomFormLabel>
                    {t("vessel-management.vessel-status-page.berthAllocation")}
                  </CustomFormLabel>
                  <Autocomplete
                    value={selectedOption}
                    onChange={(d) => {
                      setBerthAllocationArea(d?.value != null ? String(d.value) : "");
                      // Always show error if present, clear only if valid
                      const hasValue = d?.value != null && String(d.value).length > 0;
                      if (hasValue) {
                        setAdditionalErrors((p) => ({ ...p, berthAllocationArea: undefined }));
                      }
                    }}
                    options={berthAllocationOptions}
                    testId={`berth-allocation-input`}
                    placeholder={t("vessel-management.vessel-status-page.berthAllocation")}
                    error={!!additionalErrors.berthAllocationArea}
                  />
                  {additionalErrors.berthAllocationArea !== undefined && (
                    <FieldError testId="berthAllocation-error">
                      {additionalErrors.berthAllocationArea}
                    </FieldError>
                  )}
                </div>
                <div className="col-span-full mt-6 ">
                  <input type="hidden" name="sideDrawerAdditionalFileUrl" />
                  <div
                    className={
                      additionalErrors.berthPlanFile ? "border border-red-500 rounded-md p-1" : ""
                    }
                  >
                    <DocumentUpload
                      label={t("vessel-management.vessel-status-page.berthPlan")}
                      testId={"berth-plan-upload"}
                      name="berthPlanFile"
                      isError={Boolean(additionalErrors.berthPlanFile)}
                      maxFiles={1}
                      allowedExtensions={allowedSideDrawerExtensions}
                      onUpload={(newFiles) =>
                        handleFileUpload({
                          newFiles,
                          errorKey: "berthPlanFile",
                          setUploadFiles: setBerthPlanFile,
                          onUploadDocument: (file) =>
                            berthPlanUploads.mutate({ files: [file], documentType: "BERTH_PLAN" }),
                        })
                      }
                      conditions={FILE_UPLOAD_PROFILES.sideDrawerApprovalFile}
                      files={berthPlanFile}
                      disabled={berthPlanUploads.isPending}
                      setFiles={setBerthPlanFile}
                      uploadDescription={t("upload.dragOrDropDescription")}
                    />
                  </div>
                  {additionalErrors.berthPlanFile && (
                    <p style={styles.errorText} className="text-sm mt-1">
                      {additionalErrors.berthPlanFile}
                    </p>
                  )}
                  {berthPlanFile[0] && !additionalErrors.berthPlanFile && (
                    <div className="flex-1 mt-4">
                      <SingleFile
                        file={berthPlanFile[0]}
                        fieldName="berthPlanFile"
                        testId="berth-plan-upload"
                        isLoading={berthPlanUploads.isPending && berthPlanFile[0] instanceof File}
                        setFiles={() => {
                          setAdditionalErrors((prev) => ({ ...prev, berthPlanFile: undefined }));
                          setBerthPlanFile([]);
                          if (!berthPlanUploads.isPending) berthPlanUploads.reset();
                        }}
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="col-span-full mb-0 flex flex-col mt-6">
              <CustomFormLabel>
                {t("vessel-call.movement-services.close-form.fields.notes-to-agent")}
              </CustomFormLabel>
              <Input
                placeholder={t("vessel-call.movement-services.close-form.fields.notes-to-agent")}
                multiline
                minRows={4}
                fullWidth
                variant="outlined"
                value={externalNotes}
                onKeyDown={(e) => {
                  if (!isPrintableKey(e)) return;
                  if (externalNotes.length >= TEXTAREA_MAX_CHARS) {
                    setAdditionalErrors((prev) => ({
                      ...prev,
                      externalNotes: t("form-validation.max-length", {
                        label: t("vessel-call.movement-services.close-form.fields.notes-to-agent"),
                        max: TEXTAREA_MAX_CHARS,
                      }),
                    }));
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  setExternalNotes(value);
                  const result = externalNotesOnlySchema.safeParse({
                    externalNotes: value,
                  });
                  setAdditionalErrors((prev) => ({
                    ...prev,
                    externalNotes: result.success
                      ? undefined
                      : result.error.flatten().fieldErrors.externalNotes?.[0],
                  }));
                }}
                slotProps={inputSlotProps}
                sx={sxProps}
                testId={"external-notes-input"}
              />
              {additionalErrors.externalNotes && (
                <FieldError>{additionalErrors.externalNotes}</FieldError>
              )}
              <FormHelperText sx={charCounterSx}>
                {externalNotes.length}/{TEXTAREA_MAX_CHARS}
              </FormHelperText>
            </div>
            <div className="col-span-full mt-6 ">
              <input type="hidden" name="externalAttachmentsUrl" />
              <div
                className={
                  additionalErrors.externalFiles ? "border border-red-500 rounded-md p-1" : ""
                }
              >
                <DocumentUpload
                  label={t("vessel-call.movement-services.close-form.fields.attachments")}
                  testId={"external-file-upload"}
                  name="externalAttachmentsFile"
                  isError={Boolean(additionalErrors.externalFiles)}
                  maxFiles={isArrivalTerminal ? MAX_MOVEMENT_ATTACHMENTS : 1}
                  allowedExtensions={allowedSideDrawerExtensions}
                  onUpload={(newFiles) => {
                    if (!isArrivalTerminal && newFiles.length > 1) {
                      setAdditionalErrors((prev) => ({
                        ...prev,
                        externalFiles: t("upload.one-document"),
                      }));
                      return;
                    }
                    const files = getUniqueMovementUploadFiles(
                      newFiles,
                      isArrivalTerminal ? MAX_MOVEMENT_ATTACHMENTS : 1,
                    );
                    setAdditionalErrors((prev) => ({ ...prev, externalFiles: undefined }));
                    setFilesForKey("externalAttachmentsFile", files, {
                      mode: "merge",
                      maxFiles: isArrivalTerminal ? MAX_MOVEMENT_ATTACHMENTS : 1,
                    });
                    attachmentUploads.mutate({ files, documentType: "EXTERNAL_NOTES" });
                  }}
                  conditions={FILE_UPLOAD_PROFILES.sideDrawerApprovalFile}
                  files={externalAttachmentsFile}
                  disabled={attachmentUploads.isPending}
                  setFiles={setExternalAttachmentsFile}
                  uploadDescription={t("upload.dragOrDropDescription")}
                />
              </div>
              {additionalErrors.externalFiles && (
                <p style={styles.errorText} className="text-sm mt-1">
                  {additionalErrors.externalFiles}
                </p>
              )}
              {!additionalErrors.externalFiles &&
                externalAttachmentsFile.map((file, index) => (
                  <div key={`${file.name}-${index}`} className="flex-1 mt-4">
                    <SingleFile
                      file={file}
                      fieldName="externalAttachmentsFile"
                      currentFileIndex={index}
                      testId="externalAttachment-upload"
                      isLoading={attachmentUploads.isPending && file instanceof File}
                      setFiles={() => {
                        setAdditionalErrors((prev) => ({
                          ...prev,
                          externalFiles: undefined,
                        }));
                        if (!attachmentUploads.isPending) attachmentUploads.reset();
                      }}
                    />
                  </div>
                ))}
            </div>

            {!isArrivalTerminal && (
              <>
                <div className="col-span-full mb-0 flex flex-col mt-6">
                  <CustomFormLabel>
                    {t("vessel-management.vessel-status-page.internalNotes")}
                  </CustomFormLabel>
                  <Input
                    placeholder={t("vessel-management.vessel-status-page.internalNotes")}
                    multiline
                    minRows={4}
                    fullWidth
                    variant="outlined"
                    value={internalNotes}
                    onKeyDown={(e) => {
                      if (!isPrintableKey(e)) return;
                      if (internalNotes.length >= TEXTAREA_MAX_CHARS) {
                        setAdditionalErrors((prev) => ({
                          ...prev,
                          internalNotes: t("form-validation.max-length", {
                            label: t("vessel-management.vessel-status-page.internalNotes"),
                            max: TEXTAREA_MAX_CHARS,
                          }),
                        }));
                      }
                    }}
                    onBlur={() => {
                      setAdditionalErrors((prev) => ({
                        ...prev,
                        additionalDescription: undefined,
                      }));
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      setInternalNotes(value);
                      const result = internalNotesOnlySchema.safeParse({
                        internalNotes: value,
                      });
                      setAdditionalErrors((prev) => ({
                        ...prev,
                        internalNotes: result.success
                          ? undefined
                          : result.error.flatten().fieldErrors.internalNotes?.[0],
                      }));
                    }}
                    slotProps={inputSlotProps}
                    sx={sxProps}
                    testId={"additional-notes-input"}
                  />
                  {additionalErrors.internalNotes && (
                    <FieldError>{additionalErrors.internalNotes}</FieldError>
                  )}
                  <FormHelperText sx={charCounterSx}>
                    {internalNotes.length}/{TEXTAREA_MAX_CHARS}
                  </FormHelperText>
                </div>
                <div className="col-span-full mt-6 ">
                  <input type="hidden" name="sideDrawerAdditionalFileUrl" />
                  <div
                    className={
                      additionalErrors.internalAttachmentsFile
                        ? "border border-red-500 rounded-md p-1"
                        : ""
                    }
                  >
                    <DocumentUpload
                      label={t("vessel-management.vessel-status-page.internalAttachments")}
                      testId={"additional-file-upload"}
                      name="internalAttachmentsFile"
                      isError={Boolean(additionalErrors.internalAttachmentsFile)}
                      maxFiles={1}
                      allowedExtensions={allowedSideDrawerExtensions}
                      onUpload={(newFiles) =>
                        handleFileUpload({
                          newFiles,
                          errorKey: "internalAttachmentsFile",
                          setUploadFiles: setInternalAttachmentsFile,
                          onUploadDocument: (file) =>
                            internalAttachmentUploads.mutate({
                              files: [file],
                              documentType: "INTERNAL_NOTES",
                            }),
                        })
                      }
                      conditions={FILE_UPLOAD_PROFILES.sideDrawerApprovalFile}
                      files={internalAttachmentsFile}
                      disabled={internalAttachmentUploads.isPending}
                      setFiles={setInternalAttachmentsFile}
                      uploadDescription={t("upload.dragOrDropDescription")}
                    />
                  </div>
                  {additionalErrors.internalAttachmentsFile && (
                    <p style={styles.errorText} className="text-sm mt-1">
                      {additionalErrors.internalAttachmentsFile}
                    </p>
                  )}
                  {internalAttachmentsFile[0] && !additionalErrors.internalAttachmentsFile && (
                    <div className="flex-1 mt-4">
                      <SingleFile
                        file={internalAttachmentsFile[0]}
                        fieldName="internalAttachmentsFile"
                        testId="internalAttachment-upload"
                        isLoading={
                          internalAttachmentUploads.isPending &&
                          internalAttachmentsFile[0] instanceof File
                        }
                        setFiles={() => {
                          setAdditionalErrors((prev) => ({
                            ...prev,
                            internalAttachmentsFile: undefined,
                          }));
                          setInternalAttachmentsFile([]);
                          if (!internalAttachmentUploads.isPending)
                            internalAttachmentUploads.reset();
                        }}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </SideDrawer>
      {attachmentUploads.error ? (
        <Alert severity="error">{attachmentUploads.error.message}</Alert>
      ) : null}
      {berthPlanUploads.error ? (
        <Alert severity="error">{berthPlanUploads.error.message}</Alert>
      ) : null}
      {internalAttachmentUploads.error ? (
        <Alert severity="error">{internalAttachmentUploads.error.message}</Alert>
      ) : null}
    </div>
  );
};

export default ApproveMovementService;
